/**
 * ============================================================================
 * ADMIN SETTINGS PAGE
 * ============================================================================
 */

import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../hooks/useAdmin';
import { useToast, useI18n } from '../../providers';
import AdminLayout from '../../components/admin/AdminLayout';
import Button from '../../components/common/Button';
import Card from '../../components/common/Card';
import Input from '../../components/common/Input';
import Spinner from '../../components/common/Spinner';
import Tabs from '../../components/common/Tabs';

/**
 * Admin Settings Component
 * @returns {JSX.Element}
 */
const AdminSettings = () => {
  const { loadSettings, updateSystemSetting, loading } = useAdmin();
  const { showError, showSuccess } = useToast();
  const { language } = useI18n();
  const [settings, setSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('fees');

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const data = await loadSettings();
        setSettings(data);
      } catch (error) {
        showError('Failed to load settings');
        console.error('Load settings error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [loadSettings, showError]);

  const handleSave = async (key, value) => {
    try {
      await updateSystemSetting(key, value);
      showSuccess('Setting updated!');
      const data = await loadSettings();
      setSettings(data);
    } catch (error) {
      showError(error.message || 'Failed to update setting');
    }
  };

  if (isLoading || loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Spinner size="lg" />
        </div>
      </AdminLayout>
    );
  }

  if (!settings) {
    return (
      <AdminLayout>
        <Card variant="glass" className="p-12 text-center">
          <p className="text-gray-600 dark:text-gray-400">
            {language === 'fr' ? 'Aucune donnée disponible' : 'No data available'}
          </p>
        </Card>
      </AdminLayout>
    );
  }

  const tabs = [
    { id: 'fees', label: language === 'fr' ? 'Frais' : 'Fees', icon: '💰' },
    { id: 'timeframes', label: language === 'fr' ? 'Délais' : 'Timeframes', icon: '⏱️' },
    { id: 'security', label: language === 'fr' ? 'Sécurité' : 'Security', icon: '🛡️' },
  ];

  return (
    <AdminLayout>
      {/* Header */}
      <div className="mb-8 animate-fadeInUp">
        <h1 className="text-4xl font-black bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent mb-2">
          {language === 'fr' ? 'Paramètres système' : 'System Settings'}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 text-lg">
          {language === 'fr'
            ? 'Configurez les paramètres de la plateforme'
            : 'Configure platform settings'}
        </p>
      </div>

      {/* Tabs */}
      <Card variant="glass" className="mb-6 animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
        <Tabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} variant="pills" />
      </Card>

      <div className="space-y-6">
        {/* Fee Structure */}
        {activeTab === 'fees' && (
          <Card variant="glass" className="animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
            <h2 className="text-2xl font-black text-gray-900 dark:text-white mb-6 flex items-center gap-3">
              <span className="text-3xl">💰</span>
              {language === 'fr' ? 'Structure des frais' : 'Fee Structure'}
            </h2>
            <div className="space-y-6">
              {[
                {
                  key: 'transactionFee',
                  label: language === 'fr' ? 'Frais de transaction' : 'Transaction fee',
                  value: settings.transactionFee || 1.5,
                  type: 'percentage',
                  capKey: 'transactionFeeCap',
                  capValue: settings.transactionFeeCap || 15000,
                  capLabel: language === 'fr' ? 'Plafond' : 'Cap',
                  capUnit: 'XAF',
                },
                {
                  key: 'withdrawalFee',
                  label: language === 'fr' ? 'Frais de retrait' : 'Withdrawal fee',
                  value: settings.withdrawalFee || 1.0,
                  type: 'percentage',
                  minKey: 'withdrawalFeeMin',
                  minValue: settings.withdrawalFeeMin || 1000,
                  minLabel: language === 'fr' ? 'Minimum' : 'Min',
                  minUnit: 'XAF',
                },
                {
                  key: 'mediationFee',
                  label: language === 'fr' ? 'Frais de médiation' : 'Mediation fee',
                  value: settings.mediationFee || 500,
                  type: 'fixed',
                  unit: 'XAF',
                  note: language === 'fr' ? '(option de partage disponible)' : '(split option available)',
                },
              ].map((fee, index) => (
                <Card
                  key={fee.key}
                  variant="outlined"
                  className="p-4 animate-fadeInUp"
                  style={{ animationDelay: `${0.3 + index * 0.1}s` }}
                >
                  <div className="space-y-4">
                    <div className="flex items-center gap-4">
                      <label className="text-sm font-bold text-gray-900 dark:text-white flex-1">
                        {fee.label}:
                      </label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          value={fee.value}
                          onChange={(e) =>
                            setSettings({
                              ...settings,
                              [fee.key]: fee.type === 'percentage' ? parseFloat(e.target.value) : parseInt(e.target.value),
                            })
                          }
                          className="w-24"
                          step={fee.type === 'percentage' ? '0.1' : '1'}
                        />
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {fee.type === 'percentage' ? '%' : fee.unit}
                        </span>
                      </div>
                      {fee.capKey && (
                        <>
                          <span className="text-sm text-gray-600 dark:text-gray-400">{fee.capLabel}:</span>
                          <Input
                            type="number"
                            value={fee.capValue}
                            onChange={(e) =>
                              setSettings({
                                ...settings,
                                [fee.capKey]: parseInt(e.target.value),
                              })
                            }
                            className="w-32"
                          />
                          <span className="text-sm text-gray-600 dark:text-gray-400">{fee.capUnit}</span>
                        </>
                      )}
                      {fee.minKey && (
                        <>
                          <span className="text-sm text-gray-600 dark:text-gray-400">{fee.minLabel}:</span>
                          <Input
                            type="number"
                            value={fee.minValue}
                            onChange={(e) =>
                              setSettings({
                                ...settings,
                                [fee.minKey]: parseInt(e.target.value),
                              })
                            }
                            className="w-32"
                          />
                          <span className="text-sm text-gray-600 dark:text-gray-400">{fee.minUnit}</span>
                        </>
                      )}
                      {fee.note && (
                        <span className="text-xs text-gray-500 dark:text-gray-400">{fee.note}</span>
                      )}
                      <Button
                        variant="gradient"
                        size="sm"
                        onClick={() => handleSave(fee.key, settings[fee.key])}
                        className="shadow-xl hover:shadow-2xl transition-all"
                      >
                        {language === 'fr' ? 'Enregistrer' : 'Save'}
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </Card>
        )}

        {/* Transaction Timeframes */}
        {activeTab === 'timeframes' && (
          <Card variant="glass" className="animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
            <h2 className="text-2xl font-black text-gray-900 dark:text-white mb-6 flex items-center gap-3">
              <span className="text-3xl">⏱️</span>
              {language === 'fr' ? 'Délais des transactions' : 'Transaction Timeframes'}
            </h2>
            <div className="space-y-4">
              {[
                {
                  key: 'autoReleaseHours',
                  label: language === 'fr' ? 'Libération automatique après livraison' : 'Auto-release after delivery',
                  value: settings.autoReleaseHours || 72,
                  unit: language === 'fr' ? 'heures' : 'hours',
                },
                {
                  key: 'disputeResolutionDays',
                  label: language === 'fr' ? 'Période de résolution des litiges' : 'Dispute resolution period',
                  value: settings.disputeResolutionDays || 7,
                  unit: language === 'fr' ? 'jours' : 'days',
                },
                {
                  key: 'sellerResponseHours',
                  label: language === 'fr' ? 'Temps de réponse du vendeur' : 'Seller response time',
                  value: settings.sellerResponseHours || 48,
                  unit: language === 'fr' ? 'heures' : 'hours',
                },
                {
                  key: 'mediationDecisionHours',
                  label: language === 'fr' ? 'Temps de décision de médiation' : 'Mediation decision time',
                  value: settings.mediationDecisionHours || 48,
                  unit: language === 'fr' ? 'heures' : 'hours',
                },
              ].map((item, index) => (
                <Card
                  key={item.key}
                  variant="outlined"
                  className="p-4 animate-fadeInUp"
                  style={{ animationDelay: `${0.3 + index * 0.1}s` }}
                >
                  <div className="flex items-center gap-4">
                    <label className="text-sm font-bold text-gray-900 dark:text-white flex-1">
                      {item.label}:
                    </label>
                    <Input
                      type="number"
                      value={item.value}
                      onChange={(e) =>
                        setSettings({ ...settings, [item.key]: parseInt(e.target.value) })
                      }
                      className="w-24"
                    />
                    <span className="text-sm text-gray-600 dark:text-gray-400">{item.unit}</span>
                    <Button
                      variant="gradient"
                      size="sm"
                      onClick={() => handleSave(item.key, settings[item.key])}
                      className="shadow-xl hover:shadow-2xl transition-all"
                    >
                      {language === 'fr' ? 'Enregistrer' : 'Save'}
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          </Card>
        )}

        {/* Security Settings */}
        {activeTab === 'security' && (
          <Card variant="glass" className="animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
            <h2 className="text-2xl font-black text-gray-900 dark:text-white mb-6 flex items-center gap-3">
              <span className="text-3xl">🛡️</span>
              {language === 'fr' ? 'Sécurité et prévention de la fraude' : 'Security & Fraud Prevention'}
            </h2>
            <div className="space-y-6">
              {[
                {
                  key: 'dailyWithdrawalLimit',
                  label: language === 'fr' ? 'Limite de retrait quotidienne' : 'Daily withdrawal limit',
                  value: settings.dailyWithdrawalLimit || 500000,
                  unit: 'XAF',
                },
                {
                  key: 'autoFlagAmount',
                  label: language === 'fr' ? 'Signalement automatique des transactions supérieures à' : 'Auto-flag transactions over',
                  value: settings.autoFlagAmount || 200000,
                  unit: 'XAF',
                },
              ].map((item, index) => (
                <Card
                  key={item.key}
                  variant="outlined"
                  className="p-4 animate-fadeInUp"
                  style={{ animationDelay: `${0.3 + index * 0.1}s` }}
                >
                  <div className="flex items-center gap-4">
                    <label className="text-sm font-bold text-gray-900 dark:text-white flex-1">
                      {item.label}:
                    </label>
                    <Input
                      type="number"
                      value={item.value}
                      onChange={(e) =>
                        setSettings({ ...settings, [item.key]: parseInt(e.target.value) })
                      }
                      className="w-32"
                    />
                    <span className="text-sm text-gray-600 dark:text-gray-400">{item.unit}</span>
                    <Button
                      variant="gradient"
                      size="sm"
                      onClick={() => handleSave(item.key, settings[item.key])}
                      className="shadow-xl hover:shadow-2xl transition-all"
                    >
                      {language === 'fr' ? 'Enregistrer' : 'Save'}
                    </Button>
                  </div>
                </Card>
              ))}
              <Card variant="outlined" className="p-4 animate-fadeInUp" style={{ animationDelay: '0.5s' }}>
                <div className="flex items-center justify-between">
                  <label className="text-sm font-bold text-gray-900 dark:text-white">
                    {language === 'fr' ? 'Exiger 2FA pour les admins' : 'Require 2FA for admins'}:
                  </label>
                  <Switch
                    checked={settings.require2FAForAdmins || false}
                    onChange={(checked) => {
                      setSettings({ ...settings, require2FAForAdmins: checked });
                      handleSave('require2FAForAdmins', checked);
                    }}
                    variant={settings.require2FAForAdmins ? 'success' : 'default'}
                  />
                </div>
              </Card>
            </div>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;
